/**
 * DriveDayCard.jsx — Collapsible per-day summary card for the Drive Log.
 *
 * Mobile-first:
 *   - Day header row (date · miles · tag split · unassigned badge) always visible
 *   - Tap anywhere in header to expand; drives render stacked inside
 *   - Day-level actions appear inline on desktop, in a bottom action sheet on mobile
 */

import { useState, useMemo } from 'react';
import {
  Box, Typography, Chip, Stack, IconButton, Collapse, Paper,
  Button, Drawer, MenuItem, Select, FormControl, InputLabel, Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ChevronRight as CollapsedIcon,
  Business as BusinessIcon,
  Person as PersonalIcon,
  HelpOutline as UntaggedIcon,
  MoreVert as MoreIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useMobile } from '../../hooks/useMobile';
import DriveRow from './DriveRow';

function fmtDateHeader(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const fmt = (x) => x.toISOString().slice(0, 10);
  if (fmt(today) === iso) return `Today · ${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
  if (fmt(yesterday) === iso) return `Yesterday · ${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DriveDayCard({
  day,
  roster,
  defaultExpanded = false,
  onClassify,
  onAssignDriver,
  onAcceptSuggestion,
  onBulkClassify,
  onBulkAssign,
  onOpenMap,
}) {
  const { isMobile } = useMobile();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDriver, setSheetDriver] = useState('');

  const fullyReviewed = day.unconfirmedCount === 0;
  const headerBg = fullyReviewed ? 'action.hover' : 'background.paper';

  const driveIds = useMemo(() => day.drives.map((d) => d.id), [day.drives]);
  const unconfirmedIds = useMemo(
    () => day.drives.filter((d) => !d.confirmed).map((d) => d.id),
    [day.drives]
  );

  const handleDayBulk = (action) => {
    setSheetOpen(false);
    if (action === 'business' || action === 'personal') {
      if (unconfirmedIds.length) onBulkClassify(unconfirmedIds, action);
    } else if (action === 'assign' && sheetDriver) {
      if (unconfirmedIds.length) onBulkAssign(unconfirmedIds, sheetDriver);
      setSheetDriver('');
    }
  };

  const ActionButtons = (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Button
        size="small"
        variant="outlined"
        color="success"
        startIcon={<BusinessIcon />}
        disabled={!unconfirmedIds.length}
        onClick={(e) => { e.stopPropagation(); handleDayBulk('business'); }}
        sx={{ minHeight: 32 }}
      >
        All business
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        startIcon={<PersonalIcon />}
        disabled={!unconfirmedIds.length}
        onClick={(e) => { e.stopPropagation(); handleDayBulk('personal'); }}
        sx={{ minHeight: 32 }}
      >
        All personal
      </Button>
    </Stack>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.25,
        overflow: 'hidden',
        opacity: fullyReviewed ? 0.75 : 1,
        borderColor: day.unassignedCount > 0 ? 'warning.light' : 'divider',
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setExpanded((x) => !x)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: isMobile ? 1.25 : 1.5,
          cursor: 'pointer',
          bgcolor: headerBg,
          minHeight: 56,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <IconButton size="small" sx={{ p: 0.5 }}>
          {expanded ? <ExpandIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {fmtDateHeader(day.date)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {day.drives.length} drive{day.drives.length === 1 ? '' : 's'} · {day.totalMiles} mi
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            {day.businessMiles > 0 && (
              <Chip
                size="small"
                icon={<BusinessIcon sx={{ fontSize: '14px !important' }} />}
                label={`${day.businessMiles} mi`}
                color="success"
                variant="outlined"
                sx={{ height: 22 }}
              />
            )}
            {day.personalMiles > 0 && (
              <Chip
                size="small"
                icon={<PersonalIcon sx={{ fontSize: '14px !important' }} />}
                label={`${day.personalMiles} mi`}
                color="error"
                variant="outlined"
                sx={{ height: 22 }}
              />
            )}
            {day.untaggedMiles > 0 && (
              <Chip
                size="small"
                icon={<UntaggedIcon sx={{ fontSize: '14px !important' }} />}
                label={`${day.untaggedMiles} mi`}
                variant="outlined"
                sx={{ height: 22 }}
              />
            )}
            {day.unassignedCount > 0 && (
              <Chip
                size="small"
                label={`${day.unassignedCount} unassigned`}
                color="warning"
                sx={{ height: 22 }}
              />
            )}
            {fullyReviewed && (
              <Chip
                size="small"
                icon={<CheckIcon sx={{ fontSize: '14px !important' }} />}
                label="Reviewed"
                color="success"
                sx={{ height: 22 }}
              />
            )}
          </Stack>
        </Box>

        {/* Day actions */}
        {!fullyReviewed && (
          isMobile ? (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setSheetOpen(true); }}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <MoreIcon />
            </IconButton>
          ) : (
            <Box onClick={(e) => e.stopPropagation()}>{ActionButtons}</Box>
          )
        )}
      </Box>

      {/* Drives */}
      <Collapse in={expanded} unmountOnExit>
        <Divider />
        <Box>
          {day.drives.map((drive) => (
            <DriveRow
              key={drive.id}
              drive={drive}
              roster={roster}
              onClassify={onClassify}
              onAssignDriver={onAssignDriver}
              onAcceptSuggestion={onAcceptSuggestion}
              onOpenMap={onOpenMap}
            />
          ))}
        </Box>
      </Collapse>

      {/* Mobile action sheet */}
      <Drawer
        anchor="bottom"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2 } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {fmtDateHeader(day.date)} — {unconfirmedIds.length} unreviewed
        </Typography>
        <Stack spacing={1}>
          <Button
            variant="contained"
            color="success"
            startIcon={<BusinessIcon />}
            onClick={() => handleDayBulk('business')}
            disabled={!unconfirmedIds.length}
            sx={{ minHeight: 48 }}
          >
            Mark all business
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<PersonalIcon />}
            onClick={() => handleDayBulk('personal')}
            disabled={!unconfirmedIds.length}
            sx={{ minHeight: 48 }}
          >
            Mark all personal
          </Button>
          <Divider sx={{ my: 1 }} />
          <FormControl size="small" fullWidth>
            <InputLabel>Assign all unassigned to…</InputLabel>
            <Select
              label="Assign all unassigned to…"
              value={sheetDriver}
              onChange={(e) => setSheetDriver(e.target.value)}
            >
              {roster.map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={() => handleDayBulk('assign')}
            disabled={!sheetDriver || !unconfirmedIds.length}
            sx={{ minHeight: 48 }}
          >
            Assign to {roster.find((r) => r.id === sheetDriver)?.name || '…'}
          </Button>
          <Button onClick={() => setSheetOpen(false)} sx={{ minHeight: 44 }}>Cancel</Button>
        </Stack>
      </Drawer>
    </Paper>
  );
}

// Keep driveIds export for other components that may want to highlight
DriveDayCard.driveIds = (day) => (day?.drives || []).map((d) => d.id);
